import ProgressBar from "@/components/ProgressBar";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import ROICalculator from "@/components/ROICalculator";

import ServicesSection from "@/components/ServicesSection";
import WhyChooseUs from "@/components/WhyChooseUs";
import IndustriesSection from "@/components/IndustriesSection";
import HowItWorksSection from "@/components/HowItWorksSection";
import CaseStudyCards from "@/components/CaseStudyCards";
import CompareModels from "@/components/CompareModels";
import CompareCleaningRobots from "@/components/CompareCleaningRobots";
import AIMusicSection from "@/components/AIMusicSection";
import ROISnapshotSection from "@/components/ROISnapshotSection";
import IntegrationsSection from "@/components/IntegrationsSection";
import FAQSection from "@/components/FAQSection";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import DesktopStickyCTA from "@/components/DesktopStickyCTA";
import VoiceAIButton from "@/components/VoiceAIButton";
import UrgencyBanner from "@/components/UrgencyBanner";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PersonalizedBanner from "@/components/PersonalizedBanner";
import MT1SeriesSection from "@/components/MT1SeriesSection";
import AIEmployeesSection from "@/components/AIEmployeesSection";
import RobotShowcase from "@/components/RobotShowcase";
import VideoShowcase from "@/components/VideoShowcase";
import AutonomousWorkforceSection from "@/components/AutonomousWorkforceSection";
import LandscapeTeaser from "@/components/LandscapeTeaser";
import ThreeWorldsSection from "@/components/ThreeWorldsSection";
import NeilMethodSection from "@/components/NeilMethodSection";
import PropertyAutomationSection from "@/components/PropertyAutomationSection";
import PressSection from "@/components/PressSection";
import AutonomousWorkforceHeroVideo from "@/components/AutonomousWorkforceHeroVideo";

const Index = () => {
  return (
    <>
      <SEO 
        title="Neil AI Global Solutions | The RaaS Company"
        description="Neil AI Global Solutions is the RaaS company — we design, deploy and manage autonomous workforces using commercial robotics, AI employees and Robotics-as-a-Service."

        keywords="Robotics-as-a-Service, autonomous workforce, commercial robotics, enterprise robotics, AI employees, AI automation, cleaning robots, delivery robots, humanoid robots, landscape robots, industrial robotics, facility automation, robot fleet management"
      />
      <ProgressBar />
      <Navigation />
      
      <div 
        className="min-h-screen pb-4 md:pb-6 relative overflow-hidden"
        style={{
          paddingTop: "var(--app-header-h, 80px)",
          background: `
            radial-gradient(1400px 900px at 15% 5%, hsl(169 100% 25% / 0.15) 0%, transparent 55%),
            radial-gradient(1100px 700px at 85% 75%, hsl(43 62% 40% / 0.12) 0%, transparent 55%),
            linear-gradient(180deg, hsl(210 83% 8%) 0%, hsl(210 83% 4%) 100%)
          `
        }}
      >
        {/* Animated grid background */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--primary) / 0.1) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--primary) / 0.1) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />
        <div className="relative animate-fade-in">
          <HeroSection />
          <AutonomousWorkforceHeroVideo />
        </div>

        <main 
          className="home-content max-w-7xl mx-auto border-neon rounded-[16px] p-4 sm:p-6 md:p-8 lg:p-10 relative glass-strong"
          style={{
            boxShadow: "0 0 80px hsl(var(--primary) / 0.25), 0 8px 32px rgba(0, 0, 0, 0.6), inset 0 0 80px hsl(var(--primary) / 0.03)",
            transform: "translateZ(0)",
            animation: "fade-in 0.6s ease-out",
          }}
        >
          {/* Urgency Banner */}
          <UrgencyBanner />

          {/* Three Worlds — breadth of automation */}
          <ThreeWorldsSection />

          {/* The N.E.I.L. Method — how Neil AI works */}
          <NeilMethodSection showLifecycle />

          {/* Autonomous Workforce pillars: RaaS / Robotics / AI Workforce / Enterprise */}
          <AutonomousWorkforceSection />

          {/* Personalized Banner for returning visitors */}
          <PersonalizedBanner />

          {/* Robotics Solutions */}
          <RobotShowcase />

          {/* Landscape & Humanoid Divisions */}
          <LandscapeTeaser />

          {/* AI Solutions */}
          <AIEmployeesSection />

          {/* Why Choose Us */}
          <WhyChooseUs />

          {/* Industries Served */}
          <IndustriesSection />

          {/* Video Demonstrations */}
          <VideoShowcase />

          {/* PUDU MT1 Series */}
          <MT1SeriesSection />

          {/* Compact CTA */}
          <CTASection variant="compact" />

          {/* How It Works */}
          <HowItWorksSection />

          {/* Case Studies */}
          <CaseStudyCards />

          {/* ROI Calculator — moved below case studies */}
          <div id="neil-roi" className="my-10 md:my-14">
            <ROICalculator />
          </div>

          {/* Property-wide automation cross-sell */}
          <PropertyAutomationSection />

          {/* Press & Media */}
          <PressSection />

          {/* About Neil AI — concise intro, full detail on the About & Team pages */}
          <section aria-labelledby="about-neil-ai" className="py-16 md:py-20">
            <div className="mx-auto max-w-3xl text-center">
              <p className="mb-4 text-xs font-bold tracking-[0.32em] text-primary">ABOUT NEIL AI</p>
              <h2 id="about-neil-ai" className="font-orbitron text-3xl font-bold leading-tight md:text-4xl">
                A robotics partner, not just a supplier
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                Neil AI Global Solutions helps organizations select, deploy and support commercial robots and AI
                automation for cleaning, delivery, hospitality, facilities and more. Our team handles site assessment,
                mapping, staff training and ongoing service so your operation keeps running.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Button asChild size="lg" className="min-h-12 rounded-xl px-6">
                  <Link to="/about">About Neil AI</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="min-h-12 rounded-xl px-6">
                  <Link to="/team">Meet the Team</Link>
                </Button>
              </div>
            </div>
          </section>

          {/* Compare Models */}
          <CompareModels />

          {/* Compare Cleaning Robots */}
          <CompareCleaningRobots />

          {/* AI Music */}
          <AIMusicSection />

          {/* ROI Snapshot */}
          <ROISnapshotSection />

          {/* Final CTA */}
          <CTASection />

          {/* Integrations Section */}
          <IntegrationsSection />

          {/* FAQ Section */}
          <FAQSection />

          {/* Contact Section */}
          <ContactSection />

          {/* Footer */}
          <Footer />
        </main>
      </div>

      {/* Mobile Voice Widget */}
      <VoiceAIButton />

      {/* Mobile Sticky CTA */}
      <MobileStickyCTA />
      
      {/* Desktop Sticky CTA */}
      <DesktopStickyCTA />
      
      {/* Exit Intent Popup */}
      <ExitIntentPopup />
    </>
  );
};

export default Index;
